import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { statsAPI, documentoAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Link } from 'react-router-dom';
import {
    FileText,
    Clock,
    CheckCircle,
    XCircle,
    Truck,
    Plus,
    ArrowRight,
    TrendingUp,
    AlertTriangle,
} from 'lucide-react';

export default function DashboardPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [recentDocs, setRecentDocs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            const [statsRes, docsRes] = await Promise.all([
                statsAPI.get(),
                documentoAPI.getAll(),
            ]);
            setStats(statsRes.data);
            setRecentDocs(docsRes.data.slice(0, 5));
        } catch (err) {
            console.error('Error loading dashboard data:', err);
        } finally {
            setLoading(false);
        }
    };

    const statCards = [
        { 
            label: 'Total Guías', 
            value: stats?.total || 0, 
            icon: FileText, 
            color: 'text-foreground',
            bgColor: 'bg-muted'
        },
        { 
            label: 'Pendientes', 
            value: stats?.pendientes || 0, 
            icon: Clock, 
            color: 'text-slate-600',
            bgColor: 'bg-slate-100'
        },
        { 
            label: 'Autorizadas', 
            value: stats?.autorizados || 0, 
            icon: CheckCircle, 
            color: 'text-blue-600',
            bgColor: 'bg-blue-50'
        },
        { 
            label: 'Despachadas', 
            value: stats?.despachados || 0, 
            icon: Truck, 
            color: 'text-emerald-600',
            bgColor: 'bg-emerald-50'
        },
        { 
            label: 'Rechazadas', 
            value: stats?.rechazados || 0, 
            icon: XCircle, 
            color: 'text-red-600',
            bgColor: 'bg-red-50'
        },
    ];

    const quickActions = {
        admin: [
            { label: 'Ver Todas las Guías', href: '/guias/todos', icon: FileText },
            { label: 'Gestionar Usuarios', href: '/guias/usuarios', icon: Plus },
            { label: 'Carga Masiva', href: '/guias/carga-masiva', icon: TrendingUp },
        ],
        solicitante: [
            { label: 'Crear Nueva Guía', href: '/guias/crear', icon: Plus },
            { label: 'Ver Mis Guías', href: '/guias/mis-guias', icon: FileText },
        ],
        gerente: [
            { label: 'Aprobar Guías', href: '/guias/aprobar', icon: CheckCircle },
            { label: 'Ver Historial', href: '/guias/historial', icon: Clock },
        ],
        seguridad: [
            { label: 'Ir al Punto de Salida', href: '/guias/punto-salida', icon: Truck },
        ],
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                            Dashboard
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Bienvenido, {user?.nombre}
                        </p>
                    </div>
                    {user?.rol === 'solicitante' && (
                        <Link to="/guias/crear">
                            <Button className="btn-industrial bg-primary text-primary-foreground" data-testid="new-guide-btn">
                                <Plus className="h-5 w-5 mr-2" />
                                Nueva Guía
                            </Button>
                        </Link>
                    )}
                </div>

                {/* Stats Grid */}
                <div className="dashboard-grid" data-testid="stats-grid">
                    {loading ? (
                        <>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} className="h-24" />
                            ))}
                        </>
                    ) : (
                        statCards.map((stat, index) => {
                            const Icon = stat.icon;
                            return (
                                <Card 
                                    key={index} 
                                    className="stat-card border-2 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] transition-shadow"
                                    data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`p-3 rounded-sm ${stat.bgColor}`}>
                                            <Icon className={`h-6 w-6 ${stat.color}`} />
                                        </div>
                                        <div>
                                            <p className="stat-value">{stat.value}</p>
                                            <p className="stat-label">{stat.label}</p>
                                        </div>
                                    </div>
                                </Card>
                            );
                        })
                    )}
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Recent Documents */}
                    <Card className="lg:col-span-2 border-2" data-testid="recent-docs-card">
                        <CardHeader className="card-industrial-header">
                            <CardTitle className="flex items-center justify-between">
                                <span>Guías Recientes</span>
                                <Link to={user?.rol === 'solicitante' ? '/guias/mis-guias' : '/guias/todos'}>
                                    <Button variant="ghost" size="sm" className="text-xs">
                                        Ver todas
                                        <ArrowRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {loading ? (
                                <div className="p-4 space-y-4">
                                    {[1, 2, 3].map((i) => (
                                        <Skeleton key={i} className="h-16" />
                                    ))}
                                </div>
                            ) : recentDocs.length === 0 ? (
                                <div className="empty-state py-12">
                                    <FileText className="empty-state-icon" />
                                    <p className="empty-state-title">Sin guías recientes</p>
                                    <p className="empty-state-description">
                                        Las guías aparecerán aquí cuando se creen
                                    </p>
                                </div>
                            ) : (
                                <ScrollArea className="max-h-[400px]">
                                    <div className="divide-y divide-border">
                                        {recentDocs.map((doc) => (
                                            <Link 
                                                key={doc.id} 
                                                to={`/guias/documento/${doc.id}`}
                                                className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                                                data-testid={`doc-${doc.folio}`}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                                                        <FileText className="h-5 w-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <p className="font-mono text-sm font-medium">{doc.folio}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {doc.patente} • {formatDate(doc.created_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <StatusBadge estado={doc.estado} size="sm" />
                                            </Link>
                                        ))}
                                    </div>
                                </ScrollArea>
                            )}
                        </CardContent>
                    </Card>

                    {/* Quick Actions */}
                    <Card className="border-2" data-testid="quick-actions-card">
                        <CardHeader className="card-industrial-header">
                            <CardTitle>Acciones Rápidas</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 space-y-3">
                            {(quickActions[user?.rol] || []).map((action, index) => {
                                const Icon = action.icon;
                                return (
                                    <Link key={index} to={action.href}>
                                        <Button 
                                            variant="outline" 
                                            className="w-full justify-start gap-3 h-12 border-2"
                                            data-testid={`action-${action.href.replace('/', '')}`}
                                        >
                                            <Icon className="h-5 w-5" />
                                            {action.label}
                                        </Button>
                                    </Link>
                                );
                            })}
                        </CardContent>
                    </Card>
                </div>

                {/* Pending Alerts for Manager */}
                {user?.rol === 'gerente' && stats?.pendientes > 0 && (
                    <Card className="border-2 border-warning bg-warning/5" data-testid="pending-alert">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-sm bg-warning/20">
                                    <AlertTriangle className="h-6 w-6 text-warning" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-heading font-bold">
                                        {stats.pendientes} guía{stats.pendientes > 1 ? 's' : ''} pendiente{stats.pendientes > 1 ? 's' : ''} de aprobación
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        Hay guías esperando su revisión
                                    </p>
                                </div>
                                <Link to="/guias/aprobar">
                                    <Button className="btn-industrial bg-warning text-warning-foreground">
                                        Revisar
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
